// 局部替换限制图片宽高 2400 * 2400
export const replaceImageSize = 2400;

// 智能擦除限制图片宽高 2400 * 2400
export const eraseImageSize = 2400;

// 超清放大最大输出分辨率限制 (8K = 8192px)
export const MAX_UPSCALE_OUTPUT_DIMENSION = 8192;

// 4倍放大长边阈值 (长边 ≤ 2048 支持4倍放大)
export const UPSCALE_4X_THRESHOLD = 2048;

// 2倍放大长边阈值 (长边 ≤ 4096 支持2倍放大)
export const UPSCALE_2X_THRESHOLD = 4096;

/**
 * 超清放大选项
 */
export interface UpscaleOptions {
  /** 是否支持放大 */
  canUpscale: boolean;
  /** 最大支持倍数 (如 4 表示最大4倍) */
  maxFactor: number;
  /** 支持的倍数列表 */
  supportedFactors: number[];
  /** 输入图片分辨率等级: '1k' | '2k' | '4k' | '8k' */
  resolutionLevel: '1k' | '2k' | '4k' | '8k';
}

/**
 * 根据输入图片尺寸获取超清放大选项
 *
 * 重绘放大与快速放大共用此校验，确保逻辑统一（对齐 master）：
 * - 1K图 (长边 ≤ 1024): 支持 2倍、4倍放大
 * - 2K图 (1024 < 长边 ≤ 2048): 支持 2倍、4倍放大
 * - 4K图 (2048 < 长边 ≤ 4096): 仅支持 2倍放大
 * - 8K图 (长边 > 4096): 不支持放大
 *
 * 最大输出限制为 8K (8192px)，输出 = 长边 × 倍数 ≤ 8192
 *
 * @param width 图片宽度
 * @param height 图片高度
 */
export function getUpscaleOptionsByImageSize(
  width: number,
  height: number
): UpscaleOptions {
  const maxDimension = Math.max(width, height);

  // 8K 图 (长边 > 4096px): 不支持放大
  if (maxDimension > UPSCALE_2X_THRESHOLD) {
    return {
      canUpscale: false,
      maxFactor: 1,
      supportedFactors: [],
      resolutionLevel: '8k',
    };
  }

  // 4K 图 (2048 < 长边 ≤ 4096): 仅支持 2倍
  if (maxDimension > UPSCALE_4X_THRESHOLD) {
    return {
      canUpscale: true,
      maxFactor: 2,
      supportedFactors: [2],
      resolutionLevel: '4k',
    };
  }

  // 2K 图 (1024 < 长边 ≤ 2048): 支持 2倍、4倍
  if (maxDimension > 1024) {
    return {
      canUpscale: true,
      maxFactor: 4,
      supportedFactors: [2, 4],
      resolutionLevel: '2k',
    };
  }

  // 1K 图 (长边 ≤ 1024): 支持 2倍、4倍
  return {
    canUpscale: true,
    maxFactor: 4,
    supportedFactors: [2, 4],
    resolutionLevel: '1k',
  };
}
